import TileWrapper from '../TileWrapper'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useOverviewRuntime, useTile } from '../../context'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { Link } from 'react-router-dom'
import { Calendar, Users } from 'lucide-react'
import dayjs from 'dayjs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { compareTeamsByDefaultAndName } from 'app/frontend/base/helpers/teamOrdering'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'
import { linkedTeamHref } from 'app/frontend/base/helpers/linkedGuilds'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import NewsTagBadge from 'app/frontend/base/pages/News/NewsTagBadge'

type TeamSummary = NonNullable<wowaudit.OverviewData['teamSummaries']>[number]

const TeamSummaryCard = ({
  summary,
  href = summary.path,
}: {
  summary: TeamSummary
  href?: string
}) => {
  const { tile } = useTile()
  const isSidebar = tile.position === 'sidebar'
  const showRoles = !isSidebar
  const dayLabels = (summary.eventDays || []).map((day) =>
    dayjs().day(Number(day)).format('ddd'),
  )
  const eventDaysLabel = dayLabels.join(', ') || '-'

  return (
    <Card className='py-2 px-3'>
      <Link
        to={href}
        className={`min-w-0 grid ${
          showRoles
            ? 'grid-cols-3 md:grid-cols-4'
            : 'grid-cols-[minmax(0,1fr)_3rem_3rem]'
        } items-center gap-4 text-sm whitespace-nowrap overflow-hidden`}
      >
        <h5 className='m-0 truncate flex items-center gap-1.5'>
          {summary.default ? <DefaultTeamStar /> : null}
          <span className='truncate'>{summary.name}</span>
        </h5>
        {showRoles ? (
          <span className='hidden md:grid grid-cols-3 gap-1'>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='tank' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.tank}
              </span>
            </span>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='heal' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.heal}
              </span>
            </span>
            <span className='grid grid-cols-[1rem_2ch] items-center gap-1'>
              <CharacterRoleIcon role='damage' size='small' />
              <span className='tabular-nums text-right'>
                {summary.roleCounts.dps}
              </span>
            </span>
          </span>
        ) : null}
        <span className='flex items-center gap-1.5'>
          <Users className='h-4 w-4' />
          <span>{summary.characterAmount}</span>
        </span>
        <span className='flex items-center gap-1.5 truncate'>
          {isSidebar ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='flex items-center gap-1.5'>
                  <Calendar className='h-4 w-4 shrink-0' />
                  <span className='tabular-nums'>{dayLabels.length}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Weekly events are scheduled on: {eventDaysLabel}
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Calendar className='h-4 w-4 shrink-0' />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Weekly events are scheduled on these days
                </TooltipContent>
              </Tooltip>
              <span className='truncate'>{eventDaysLabel}</span>
            </>
          )}
        </span>
      </Link>
    </Card>
  )
}

const TeamSummaryTile = () => {
  const { guild } = useGuild()
  const { overviewData } = useOverviewRuntime()
  const { customDomain } = useCustomDomain()
  const gameVersion = useGameVersion()
  const visibleSummaries = [...(overviewData?.teamSummaries || [])]
    .filter((summary) => summary.visibleInGuild)
    .sort(compareTeamsByDefaultAndName)
  const teamSummaries = visibleSummaries.filter(
    (summary) => !guild || summary.guildId === guild.id,
  )
  const linkedGroups = (guild?.linkedGuilds ?? [])
    .map((linkedGuild) => ({
      linkedGuild,
      isOtherGameVersion: linkedGuild.gameVersion !== gameVersion.id,
      summaries: visibleSummaries.filter(
        (summary) => summary.guildId === linkedGuild.id,
      ),
    }))
    .filter((group) => group.summaries.length > 0)

  if (teamSummaries.length === 0 && linkedGroups.length === 0) {
    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No teams found</p>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper>
      <ul className='space-y-2'>
        {teamSummaries.map((summary) => {
          const teamReference = guild?.teamReferences.find(
            (reference) => reference.id === summary.id,
          )

          return (
            <li key={summary.id}>
              {teamReference ? (
                <ProtectedContent
                  section='base'
                  requirement='member'
                  teamReference={teamReference}
                  alwaysRenderContent={true}
                >
                  <TeamSummaryCard summary={summary} />
                </ProtectedContent>
              ) : (
                <TeamSummaryCard summary={summary} />
              )}
            </li>
          )
        })}

        {linkedGroups.map(({ linkedGuild, isOtherGameVersion, summaries }) => (
          <li key={`linked-${linkedGuild.id}`} className='pt-2'>
            <h6 className='m-0 mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground'>
              <span className='truncate'>{linkedGuild.name}</span>
              {isOtherGameVersion ? (
                <NewsTagBadge tag={linkedGuild.gameVersion} />
              ) : null}
            </h6>
            <ul className='space-y-2'>
              {summaries.map((summary) => {
                const href = linkedTeamHref(summary, {
                  isOtherGameVersion,
                  onCustomDomain: !!customDomain,
                })

                return (
                  <li key={summary.id}>
                    <TeamSummaryCard
                      summary={summary}
                      href={'external' in href ? href.external : href.internal}
                    />
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>
    </TileWrapper>
  )
}

export default TeamSummaryTile
